import './styles/ModuleDetailview.scss';

import {
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery as useGQLQuery
} from '@apollo/client';
import { ChevronLeft, CloudDownload } from '@mui/icons-material';
import {
  Button,
  Divider,
  FormControlLabel,
  Grid,
  Icon,
  Link,
  Paper,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material';
import { GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import {
  LotteryModuleResult,
  LotteryModuleResultUser,
  Module,
  ModuleSelection
} from '@thro-types/modules-selections';
import { ShibUser } from '@thro-types/users';

import { omit } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ADD_STUDENT_MUTATION from 'gql/mutations/ModuleSelectionsService/addStudentToLotteryResult.gql';
import DISCHARGE_STUDENT_MUTATION from 'gql/mutations/ModuleSelectionsService/dischargeStudent.gql';
import MUTATION from 'gql/mutations/ModulesService/deleteModule.gql';
import UPDATE_STATE from 'gql/mutations/ModulesService/updateModule.gql';
import UPDATE_MUTATION from 'gql/mutations/ModulesService/updateModule.gql';
import UPDATE_TAKING_PLACE from 'gql/mutations/ModulesService/updateTakingPlace.gql';
import DEFAULT_MODULES_QUERY from 'gql/queries/ModulesService/getDefaultModules.gql';
import QUERY from 'gql/queries/ModulesService/getModuleById.gql';
import MODULES_SELECTION_QUERY from 'gql/queries/ModulesService/getModuleSelectionById.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';

import {
  invalidateLotteryResults,
  invalidateLotteryResultsByModule,
  useLotteryResultsByModuleQuery
} from 'api/hooks/module-selection';
import { useCoursesQuery } from 'api/hooks/useCoursesQuery';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { DataGrid } from 'app/components/DataGrid';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Filters } from 'app/components/Filters';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import { ActiveState } from 'app/components/RichList/items/AWPM-FWPM/ModuleItem';
import { isModuleActive } from 'app/components/RichList/items/AWPM-FWPM/ModuleItem/helper';
import { UserSelectionModal } from 'app/components/UserSelectionModal';
import { handleDownload } from 'app/components/Util/downloadAttachement';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import i18next, { translations } from 'locales/i18n';
import { Course } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import usePageHeading from 'utils/hooks/usePageHeading';

import { hasAtLeastDischargable } from '../../ModuleSelections/helper';
import { isExactlyDischargable } from '../../ModuleSelections/helper';
import { State, SubjectType } from '../../ModuleSelections/models';
import ModuleModal from '../ModuleModal';
import { GeneralInfo } from './components/GeneralInfo';
import { MoreInformation } from './components/MoreInformation';

export enum ModuleTabPanels {
  ASSIGNED = 'assigned',
  WAITLIST = 'waitlist',
  DISCHARGED = 'discharged'
}

interface ModuleDetailViewProps {
  subjectType: SubjectType;
  onEdit?: () => void;
}

export function ModuleDetailView(props: ModuleDetailViewProps) {
  const client = useApolloClient();
  const { id } = useParams<string>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ModuleTabPanels>(ModuleTabPanels.ASSIGNED);
  const [signOutModalOpen, setSignOutModalOpen] = useState(false);
  const [addStudentModalOpen, setAddStudentModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [changeStateModalOpen, setChangeStateModalOpen] = useState(false);
  const [currentlyEditingRow, setCurrentlyEditingRow] = useState<LotteryModuleResultUser>();
  const [isNotTakingPlaceModalOpen, setIsNotTakingPlaceModalOpen] = useState(false);
  const [showStudentsWithEnoughDesiredModules, setShowStudentsWithEnoughDesiredModules] =
    useState(false);

  const userData = client.readQuery({ query: USER_QUERY });
  const aclHelper = new ACLHelper(userData.currentUser);
  const isStudentView = aclHelper.isStudent(true);

  usePageHeading({
    heading: (location?.state?.back as string)?.includes('selections')
      ? t(translations.pages.modules.goBackToSelections())
      : t(translations.pages.modules.goBackFromDetailView()),
    headingProps: {
      back: location.state?.back ?? `/${props.subjectType.toLowerCase()}/modules`
    }
  });

  function allowedToEdit(courses: Course[], selection: ModuleSelection, moduleById: Module) {
    if (selection?.state === State.Archived) {
      return false;
    }
    if (props.subjectType === SubjectType.Awpm) {
      return aclHelper.isSubjectSelectionAdmin(false, SubjectType.Awpm);
    } else if (!!selection) {
      return (
        aclHelper
          .getSelectionAdminCourses(courses)
          .map(course => course.id)
          .includes(selection.courseId!) || aclHelper.isSuperUser()
      );
    } else {
      return (
        !!aclHelper
          .getSelectionAdminCourses(courses)
          .find(course => course.id === moduleById.responsibleCourseOfStudy) ||
        aclHelper.isSuperUser()
      );
    }
  }

  const [deleteModule] = useMutation(MUTATION, {
    context: { clientName: 'federation' }
  });

  const getRowClassName = (params: GridRowParams) =>
    params.row.receivedDesiredNumberOfModules ? 'receivedDesiredNumberOfModules' : '';

  function handleCancelAddStudent() {
    setAddStudentModalOpen(false);
  }

  const [dischargeStudent] = useMutation(DISCHARGE_STUDENT_MUTATION, {
    context: { clientName: 'federation' }
  });

  const getModuleByIdQuery = useGQLQuery<any, any>(QUERY, {
    context: { clientName: 'federation' },
    variables: { id: parseInt(id!, 10) }
  });

  const [moduleSelection, selection] = useLazyQuery<any, any>(MODULES_SELECTION_QUERY);

  if (
    !getModuleByIdQuery.loading &&
    !!getModuleByIdQuery.data?.getModuleById.selectionId &&
    !selection.loading &&
    !selection.data
  ) {
    moduleSelection({
      context: { clientName: 'federation' },
      variables: { id: parseInt(getModuleByIdQuery.data?.getModuleById?.selectionId, 10) }
    });
  }

  const [addStudent] = useMutation(ADD_STUDENT_MUTATION, {
    context: { clientName: 'federation' }
  });

  const { data: lotteryResults, isLoading } = useLotteryResultsByModuleQuery(
    parseInt(id || '0'),
    !isStudentView
  );
  const { data: courses } = useCoursesQuery();

  const modifiedLotteryResults = (lotteryResults: LotteryModuleResult | undefined) => {
    const addFields = (list: LotteryModuleResultUser[]) =>
      list.map(lr => ({
        ...lr,
        // receivedDesiredNumberOfModules: false // TODO
        courseOfStudy: courses.find(course => course.id === lr.courseOfStudyId)
      }));

    return {
      assigned: addFields(lotteryResults?.assigned || []),
      waitlist: addFields(lotteryResults?.waitlist || []),
      discharged: addFields(lotteryResults?.discharged || [])
    };
  };

  const [updateTakingPlace] = useMutation(UPDATE_TAKING_PLACE, {
    context: { clientName: 'federation' }
  });
  const [updateState] = useMutation(UPDATE_STATE, {
    context: { clientName: 'federation' }
  });
  const [updateModule] = useMutation(UPDATE_MUTATION, {
    context: { clientName: 'federation' }
  });
  const dispatch = useDispatch();

  const handleDelete = async module => {
    const { errors } = await deleteModule({
      variables: {
        id: parseInt(module.id, 10)
      },
      refetchQueries: [DEFAULT_MODULES_QUERY]
    });
    if (errors) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.deleteFailed())));
    } else {
      dispatch(AlertActions.success(t(translations.pages.modules.alerts.deleteSuccessful())));
      navigate(-1);
    }
  };

  const handleDischarge = async (forceMoveUp?: boolean) => {
    if (!currentlyEditingRow) {
      return;
    }
    const { errors } = await dischargeStudent({
      variables: {
        modulePreferenceId: currentlyEditingRow.modPrefId,
        forceMoveUp: forceMoveUp
      }
    });
    invalidateLotteryResultsByModule(queryClient);
    invalidateLotteryResults(queryClient);
    if (errors) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.dischargeStudentFailed())));
    } else {
      dispatch(
        AlertActions.success(t(translations.pages.modules.alerts.dischargeStudentSuccessful()))
      );
    }
  };

  const handleChangeIsTakingPlace = async state => {
    const { errors } = await updateTakingPlace({
      variables: {
        id: parseInt(id!, 10),
        state: state
      },
      refetchQueries: [
        {
          query: QUERY,
          variables: { id: parseInt(id!, 10) },
          context: { clientName: 'federation' }
        }
      ]
    });
    if (errors) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.changeStateFailed())));
    } else {
      dispatch(AlertActions.success(t(translations.pages.modules.alerts.changeStateSuccssful())));
    }
  };

  //The filter will be maybe reused in the future, currently it is not properly connected to the backend
  const richlistFilter = () => [
    // {
    //   field: 'lastName',
    //   label: t(translations.pages.modules.studentNumber()),
    //   stateHandler: [keywordInputState, setKeywordInputState]
    // }
  ];

  const shouldActionColumnBeVisible = (hasRightToEdit: boolean) => {
    const selectionState = selection?.data?.getModuleSelectionById.state;
    return (
      hasRightToEdit &&
      (selectionState === State.Dischargable || selectionState === State.Completed)
    );
  };

  const renderDataGrid = (rows: LotteryModuleResultUser[], hasRightToEdit: boolean) => (
    <DataGrid
      style={{ height: '100%', width: '100%' }}
      disableColumnFilter
      disableRowSelectionOnClick
      columns={columns}
      getRowId={(item: LotteryModuleResultUser) => item.userId!.toString() + item.uniqueCode}
      rows={rows}
      rowCount={rows?.length}
      paginationMode={'client'}
      checkboxSelection={false}
      columnVisibilityModel={{
        actions: shouldActionColumnBeVisible(hasRightToEdit)
      }}
      className="moduleDetailView__dataGridWrapper"
      getRowClassName={activeTab === ModuleTabPanels.WAITLIST ? getRowClassName : undefined}
      loading={isLoading}
    />
  );

  const renderTabContent = (
    lotteryResults: LotteryModuleResult | undefined,
    hasRightToEdit: boolean
  ) => {
    const rows = modifiedLotteryResults(lotteryResults);

    const filteredWaitlistRows = () => {
      if (!showStudentsWithEnoughDesiredModules) {
        return rows.waitlist.filter(row => !row.receivedDesiredNumberOfModules);
      } else return rows.waitlist;
    };

    switch (activeTab) {
      case ModuleTabPanels.ASSIGNED:
        return renderDataGrid(rows.assigned, hasRightToEdit);
      case ModuleTabPanels.WAITLIST:
        return renderDataGrid(filteredWaitlistRows(), hasRightToEdit);
      case ModuleTabPanels.DISCHARGED:
        return renderDataGrid(rows.discharged, hasRightToEdit);
      default:
        return null;
    }
  };

  const renderDischargeDialog = (module: Module) => {
    if (
      selection?.data?.getModuleSelectionById.state === State.Completed &&
      (!module.isMoveUpActive || module.pauseSuccessor)
    ) {
      return (
        <ConfirmationDialog
          onClose={() => setSignOutModalOpen(false)}
          title={t(translations.pages.studentView.signOutDialogTitle())}
          open={signOutModalOpen}
          customConfirm={
            <>
              <Button
                variant="contained"
                onClick={() => {
                  setSignOutModalOpen(false);
                  handleDischarge();
                }}
              >
                {t(translations.pages.modules.dischargeConfirmOnlyDischarge())}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setSignOutModalOpen(false);
                  handleDischarge(true);
                }}
              >
                {t(translations.pages.modules.dischargeConfirmMoveUp())}
              </Button>
            </>
          }
        >
          {t(translations.pages.modules.dischargeDialogTextPausedSuccesor())}
        </ConfirmationDialog>
      );
    } else if (
      selection?.data?.getModuleSelectionById.state === State.Dischargable &&
      (!module.isMoveUpActive || module.pauseSuccessor)
    ) {
      return (
        <ConfirmationDialog
          onClose={() => setSignOutModalOpen(false)}
          onConfirm={() => {
            setSignOutModalOpen(false);
            handleDischarge();
          }}
          title={t(translations.pages.studentView.signOutDialogTitle())}
          open={signOutModalOpen}
        >
          {t(translations.pages.modules.dischargeDialogTextNotMovingUp())}
        </ConfirmationDialog>
      );
    } else {
      return (
        <ConfirmationDialog
          onClose={() => setSignOutModalOpen(false)}
          onConfirm={() => {
            setSignOutModalOpen(false);
            handleDischarge();
          }}
          title={t(translations.pages.studentView.signOutDialogTitle())}
          open={signOutModalOpen}
        >
          {t(translations.pages.modules.dischargeDialogText())}
        </ConfirmationDialog>
      );
    }
  };

  const rowActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-trash-alt"
              style={{
                width: 'auto',
                color: 'red'
              }}
            />
          }
          label={t(translations.pages.modules.signOut())}
          onClick={() => {
            setCurrentlyEditingRow(params.row);
            setSignOutModalOpen(true);
          }}
          showInMenu
          disabled={activeTab === ModuleTabPanels.DISCHARGED}
        />
      ];
    },
    [activeTab, t]
  );

  const columns = useMemo((): GridColDef[] => {
    return [
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.modules.studentNumber()),
        field: 'uniqueCode',
        renderCell: params => (
          <Tooltip title={params.row.uniqueCode}>
            <span>{params.row.uniqueCode}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 150,
        headerName: t(translations.pages.modules.table.firstName()),
        field: 'firstName',
        renderCell: params => (
          <Tooltip title={params.row.firstName}>
            <span>{params.row.firstName}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 150,
        headerName: t(translations.pages.modules.table.lastName()),
        field: 'lastName',
        renderCell: params => (
          <Tooltip title={params.row.lastName}>
            <span>{params.row.lastName}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 100,
        headerName: t(translations.pages.modules.table.courseOfStudy()),
        field: 'courseOfStudy',
        renderCell: params => {
          return (
            <Tooltip title={params.row?.courseOfStudyShorthand}>
              <span>{params.row?.courseOfStudyShorthand}</span>
            </Tooltip>
          );
        }
      },
      {
        flex: 1,
        minWidth: 100,
        headerName: t(translations.pages.modules.table.semester()),
        field: 'semester',
        renderCell: params => (
          <Tooltip title={params.row.semester ?? ''}>
            <span>{params.row.semester}</span>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: 200,
        headerName: t(translations.pages.modules.table.wishlistPosition()),
        field: 'wishlistPosition',
        renderCell: params => (
          <Tooltip title={params.row?.wishlistPosition ?? ''}>
            <span>{params.row?.wishlistPosition ?? '0'}</span>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: 100,
        headerName: t(translations.pages.modules.table.drawnNumber()),
        field: 'drawnNumber',
        renderCell: params => (
          <Tooltip title={params.row.drawnNumber}>
            <span>{params.row.drawnNumber}</span>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: params => rowActions(params)
      }
    ];
  }, [rowActions, t]);

  const handleChangeState = async module => {
    const { errors } = await updateState({
      variables: {
        id: Number(module.id),
        data: {
          activeState: isModuleActive(module) ? ActiveState.Inactive : ActiveState.Active
        }
      },
      refetchQueries: [
        {
          query: QUERY,
          variables: { id: parseInt(id!, 10) },
          context: { clientName: 'federation' }
        },
        {
          query: DEFAULT_MODULES_QUERY,
          variables: { subjectType: props.subjectType },
          context: { clientName: 'federation' }
        }
      ]
    });
    if (errors) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.changeStateFailed())));
    } else {
      dispatch(AlertActions.success(t(translations.pages.modules.alerts.changeStateSuccssful())));
    }
  };

  //If the selection has at least the state dischargable, it also has a lottery
  const isAddStudentButtonDisabled: boolean = !hasAtLeastDischargable(
    selection?.data?.getModuleSelectionById
  );

  //If the selection is exactly in state dischargeable we can show the button to pause successors
  const isPauseSuccessorButtonDisabled: boolean = !isExactlyDischargable(
    selection?.data?.getModuleSelectionById
  );

  const handleSavePauseSuccessor = async (pauseSuccessor: boolean) => {
    try {
      await updateModule({
        variables: {
          id: parseInt(id!, 10),
          data: {
            pauseSuccessor
          }
        },
        context: { clientName: 'federation' },
        refetchQueries: [
          {
            query: QUERY,
            variables: { id: parseInt(id!, 10) },
            context: { clientName: 'federation' }
          },
          {
            query: DEFAULT_MODULES_QUERY,
            variables: { subjectType: props.subjectType },
            context: { clientName: 'federation' }
          }
        ]
      });
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      dispatch(
        AlertActions.success(
          pauseSuccessor
            ? t(translations.pages.modules.alerts.successfulPauseDischarge())
            : t(translations.pages.modules.alerts.successfulContinueDischarge())
        )
      );
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.failedUpdate())));
    }
  };

  const Body = () => (
    <GQLGuard
      query={[getModuleByIdQuery]}
      lazyQuery={[selection]}
      onError={error => {
        if (error?.graphQLErrors[0]?.extensions?.status === 404) {
          dispatch(
            AlertActions.error(`${t(translations.pages.modules.alerts.failedLoadingModule())}`)
          );
        } else {
          dispatch(
            AlertActions.error(
              `${t(translations.pages.modules.alerts.failedLoadingModule())}: ${error?.message}`
            )
          );
        }
        navigate(`/${props.subjectType.toLowerCase()}/modules`, { replace: true });
        return null;
      }}
    >
      {([moduleById]) => {
        const module: Module = moduleById?.getModuleById;
        const moduleSelection = selection?.data?.getModuleSelectionById;

        const hasRightToEdit: boolean = allowedToEdit(courses, moduleSelection, module);

        const handleAddStudentSubmit = async (values: ShibUser) => {
          try {
            const user = omit(values, ['__typename']);

            handleCancelAddStudent();
            await addStudent({
              variables: {
                lotteryId: lotteryResults?.lotteryId,
                moduleSelectionId: Number(moduleSelection.id),
                moduleId: module.id,
                user
              }
            });
            invalidateLotteryResultsByModule(queryClient);
            invalidateLotteryResults(queryClient);
            dispatch(
              AlertActions.success(t(translations.pages.modules.alerts.addStudentSuccessful()))
            );
          } catch (err) {
            dispatch(AlertActions.error(t(translations.pages.modules.alerts.addStudentFailed())));
          }
        };

        const additionalFilter = () => {
          if (module.participantsConfigurations.some(pc => pc.isGlobal)) return {};

          const selectedCourses = module.participantsConfigurations.flatMap(pc =>
            courses
              .filter(course => pc.courseOfStudyIds.includes(course.id))
              .map(course => course.shorthand)
          );

          return moduleSelection?.state !== State.Completed
            ? { courseShorthands: selectedCourses }
            : {};
        };

        return (
          <Grid component={Paper} container>
            <Grid container item xs={12}>
              <GeneralInfo
                module={module}
                courses={courses}
                lotteryResults={lotteryResults}
                subjectType={props.subjectType}
                isLoading={isLoading}
                shouldShowAssignedCount={
                  (hasAtLeastDischargable(selection?.data?.getModuleSelectionById) &&
                    selection.data?.getModuleSelectionById?.publishedAt != null) ||
                  !isStudentView
                }
              />
              <MoreInformation
                module={module}
                handleChangeIsTakingPlace={handleChangeIsTakingPlace}
                isStudentView={isStudentView}
                showControlButtons={
                  //! The studentView has its own checks to hide the button
                  hasRightToEdit || isStudentView
                }
                userData={userData.currentUser}
                props={props}
                courses={courses}
                setChangeStateModalOpen={setChangeStateModalOpen}
                setDeleteModalOpen={setDeleteModalOpen}
                setEditModalOpen={setEditModalOpen}
                setIsNotTakingPlaceModalOpen={setIsNotTakingPlaceModalOpen}
                selection={moduleSelection}
              />
            </Grid>
            {!isStudentView && !!module.selectionId && (
              <Grid
                item
                container
                xs={12}
                className="moduleDetailView__moreInformation"
                spacing={2}
              >
                <Grid item container justifyContent="space-between">
                  <Grid item>
                    <Typography component="h2">
                      {t(translations.pages.modules.listOfParticipants())}
                    </Typography>
                  </Grid>
                  <Grid item>
                    {lotteryResults?.createdAt && moduleSelection?.state !== State.Completed && (
                      <Grid container alignItems="center" justifyContent="center">
                        <Icon className="fas fa-hourglass" />
                        <Typography paddingLeft={1}>
                          {t(translations.pages.moduleSelectionLottery.lastUpdate(), {
                            date: new Date(lotteryResults?.createdAt).toLocaleString(
                              i18next.language ?? i18next.languages[0] ?? 'de-DE',
                              {
                                hour: '2-digit',
                                minute: '2-digit',
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              }
                            )
                          })}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Filters
                    filterConfig={richlistFilter()}
                    onFilterChange={undefined}
                    controls={
                      <Grid container style={{ display: 'flex', alignItems: 'center' }}>
                        {
                          <>
                            <Button
                              startIcon={<CloudDownload />}
                              variant="outlined"
                              onClick={() =>
                                handleDownload(
                                  module.name,
                                  `${
                                    import.meta.env.VITE_GATEWAY_URL
                                  }/exports/module-selections/module/${id}`
                                )
                              }
                            >
                              {t(translations.common.export())}
                            </Button>
                            {hasRightToEdit && module.isMoveUpActive && (
                              <Tooltip
                                title={
                                  isPauseSuccessorButtonDisabled
                                    ? t(translations.pages.modules.alerts.noSuccessorPossible())
                                    : ''
                                }
                              >
                                <span>
                                  <Button
                                    startIcon={
                                      <Icon
                                        className={`fas ${
                                          module.pauseSuccessor ? 'fa-play' : 'fa-pause'
                                        }`}
                                        style={{ color: 'white' }}
                                      />
                                    }
                                    variant="contained"
                                    color="primary"
                                    style={{ color: 'white', marginLeft: '0.5em' }}
                                    onClick={() => {
                                      handleSavePauseSuccessor(!module.pauseSuccessor);
                                    }}
                                    disabled={isPauseSuccessorButtonDisabled}
                                  >
                                    {module.pauseSuccessor
                                      ? t(translations.pages.modules.resumeSuccessor())
                                      : t(translations.pages.modules.pauseSuccessor())}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            {hasRightToEdit && (
                              <Tooltip
                                title={
                                  isAddStudentButtonDisabled
                                    ? t(translations.pages.modules.alerts.noLotteryToAddTo())
                                    : ''
                                }
                              >
                                <span>
                                  <Button
                                    startIcon={
                                      <Icon className="fas fa-plus" style={{ color: 'white' }} />
                                    }
                                    variant="contained"
                                    color="primary"
                                    style={{ color: 'white', marginLeft: '0.5em' }}
                                    onClick={() => {
                                      return setAddStudentModalOpen(true);
                                    }}
                                    disabled={isAddStudentButtonDisabled}
                                  >
                                    {t(translations.pages.modules.addStudent())}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </>
                        }
                      </Grid>
                    }
                  />
                  <Grid
                    container
                    item
                    flexGrow={1}
                    style={{ height: `70vh`, width: '100%', marginBottom: '2em' }}
                    justifyContent={'space-between'}
                  >
                    <Tabs
                      value={activeTab}
                      onChange={(_event, value: ModuleTabPanels) => setActiveTab(value)}
                      aria-label="preview/occupancy/studentlist tabs"
                      indicatorColor="primary"
                    >
                      <Tab
                        value={ModuleTabPanels.ASSIGNED}
                        label={t(translations.pages.modules.listOfParticipants())}
                      />
                      <Tab
                        value={ModuleTabPanels.WAITLIST}
                        label={t(translations.pages.studentView.tabs.waitingList())}
                      />
                      <Tab
                        value={ModuleTabPanels.DISCHARGED}
                        label={t(translations.pages.modules.listOfDischarged())}
                      />
                    </Tabs>
                    {activeTab === ModuleTabPanels.WAITLIST && (
                      <FormControlLabel
                        value={showStudentsWithEnoughDesiredModules}
                        checked={showStudentsWithEnoughDesiredModules}
                        onChange={(_, checked) => {
                          setShowStudentsWithEnoughDesiredModules(checked);
                        }}
                        control={<Switch />}
                        label={t(translations.pages.modules.showStudentsWithEnoughDesiredModules())}
                        labelPlacement="start"
                        style={{ marginRight: 15 }}
                      />
                    )}
                    {renderTabContent(lotteryResults, hasRightToEdit)}
                  </Grid>
                </Grid>
              </Grid>
            )}
            <ErrorBoundary boundaryTag="moduleDetailBoundary">
              <UserSelectionModal
                open={addStudentModalOpen}
                setOpen={() => setAddStudentModalOpen(false)}
                handleSubmit={handleAddStudentSubmit}
                additionalFilter={additionalFilter()}
                title={t(translations.pages.modules.addStudent())}
              />
              <Modal
                open={editModalOpen}
                onClose={(_event, reason) => {
                  if (reason && reason === 'backdropClick') return;
                  setCancelModalOpen(true);
                }}
                headline={t(
                  translations.pages.modules[!module.id ? 'create' : 'edit'].title[
                    props.subjectType.toLowerCase()
                  ]()
                )}
                disabledCancel={disabledCancel}
                onCancel={() => {
                  setDisabledCancel(true);
                  setCancelModalOpen(true);
                  setDisabledCancel(false);
                }}
              >
                <ModuleModal
                  module={moduleById.getModuleById}
                  closeHandler={() => {
                    setEditModalOpen(false);
                    props.onEdit && props.onEdit();
                  }}
                  setDisabledCancel={setDisabledCancel}
                  subjectType={props.subjectType}
                  courses={courses}
                />
              </Modal>
            </ErrorBoundary>
            <ConfirmationDialog
              open={isNotTakingPlaceModalOpen}
              title={t(translations.pages.modules.isNotTakingPlaceDialogTitle())}
              onClose={() => {
                setIsNotTakingPlaceModalOpen(false);
              }}
              onConfirm={() => {
                setIsNotTakingPlaceModalOpen(false);
                handleChangeIsTakingPlace(false);
              }}
            >
              <div>{t(translations.pages.modules.isNotTakingPlaceDialoginfo())}</div>
            </ConfirmationDialog>
            <ConfirmationDialog
              open={deleteModalOpen}
              title={t(translations.pages.modules.deleteConfirm.title())}
              onClose={() => {
                setDeleteModalOpen(false);
              }}
              onConfirm={() => {
                setDeleteModalOpen(false);
                handleDelete(module);
              }}
            >
              <div>{t(translations.pages.modules.deleteConfirm.message())}</div>
            </ConfirmationDialog>
            <ConfirmationDialog
              open={cancelModalOpen}
              title={t(translations.common.cancelConfirm.title())}
              onClose={() => {
                setCancelModalOpen(false);
              }}
              onConfirm={() => {
                setEditModalOpen(false);
                setCancelModalOpen(false);
              }}
            >
              <div>{t(translations.common.cancelConfirm.message())}</div>
            </ConfirmationDialog>
            <ConfirmationDialog
              open={changeStateModalOpen}
              title={
                isModuleActive(module)
                  ? t(translations.pages.modules.setStateConfirm.setInavctiveTitle())
                  : t(translations.pages.modules.setStateConfirm.setActiveTitle())
              }
              onClose={() => {
                setChangeStateModalOpen(false);
              }}
              onConfirm={() => {
                setChangeStateModalOpen(false);
                handleChangeState(module);
              }}
            >
              <div>
                {isModuleActive(module)
                  ? t(translations.pages.modules.setStateConfirm.setInactive())
                  : t(translations.pages.modules.setStateConfirm.setActive())}
              </div>
            </ConfirmationDialog>
            {renderDischargeDialog(module)}
          </Grid>
        );
      }}
    </GQLGuard>
  );

  return (
    <Grid container alignContent={'flex-start'}>
      {isMobile && (
        <>
          <Grid container item>
            <Link onClick={() => navigate(-1)} style={{ margin: 10 }}>
              <Grid container>
                <ChevronLeft />
                <Typography>{t(translations.common.back())}</Typography>
              </Grid>
            </Link>
          </Grid>
          <Divider style={{ width: '100%' }} />
        </>
      )}
      <Body />
    </Grid>
  );
}
